import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useBlogStatus } from "@/lib/useBlogStatus";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/wat-is-human-design", label: "Wat is Human Design" },
    { to: "/methodes", label: "Methodes" },
    { to: "/aanbod", label: "Aanbod" },
    { to: "/over-marcel", label: "Over Marcel" },
    { to: "/contact", label: "Contact" },
];

const slugify = (s) =>
    s
        .toLowerCase()
        .replace(/&/g, "en")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();
    // Blog-item verschijnt pas als er een gepubliceerd artikel is
    const { showBlog } = useBlogStatus();
    const nav = showBlog ? [...NAV, { to: "/blog", label: "Blog" }] : NAV;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
                    scrolled
                        ? "bg-[var(--hds-sand)]/85 backdrop-blur-md border-b border-[var(--hds-line)]"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-[76px] flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="header-wordmark"
                        className="group flex items-baseline gap-2 select-none"
                        aria-label="Human Design Studio, naar home"
                    >
                        <span
                            aria-hidden
                            className="inline-block w-2.5 h-2.5 rounded-full bg-[var(--hds-clay)] translate-y-[-2px] transition-transform duration-500 group-hover:scale-125"
                        />
                        <span
                            className="font-[Fraunces] text-[19px] md:text-[21px] tracking-tight text-[var(--hds-ink)]"
                            style={{ fontWeight: 400 }}
                        >
                            Human Design{" "}
                            <em
                                className="not-italic text-[var(--hds-clay)]"
                                style={{ fontStyle: "italic", fontWeight: 300 }}
                            >
                                Studio
                            </em>
                        </span>
                    </Link>

                    <nav
                        className="hidden lg:flex items-center gap-8"
                        aria-label="Hoofdnavigatie"
                    >
                        {nav.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-link-${slugify(item.label)}`}
                                className={({ isActive }) =>
                                    `relative text-[13.5px] tracking-wide transition-colors duration-300 ${
                                        isActive
                                            ? "text-[var(--hds-clay)]"
                                            : "text-[var(--hds-ink)]/75 hover:text-[var(--hds-ink)]"
                                    }`
                                }
                                end={item.to === "/"}
                            >
                                {({ isActive }) => (
                                    <span className="inline-block relative">
                                        {item.label}
                                        <span
                                            className={`absolute -bottom-1 left-0 h-px bg-[var(--hds-clay)] transition-all duration-500 ${
                                                isActive ? "w-full" : "w-0"
                                            }`}
                                        />
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/boeken"
                            data-testid="header-cta-boek-sessie"
                            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[var(--hds-clay)] hover:bg-[var(--hds-clay-dark)] text-[var(--hds-card)] px-5 py-2.5 text-[13.5px] tracking-wide transition-colors duration-500"
                        >
                            <span>Boek een sessie</span>
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                aria-hidden
                            >
                                <path
                                    d="M2 7h10M8 3l4 4-4 4"
                                    stroke="currentColor"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen((v) => !v)}
                            data-testid="mobile-menu-toggle"
                            aria-label={open ? "Sluit menu" : "Open menu"}
                            aria-expanded={open}
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[var(--hds-line)] text-[var(--hds-ink)] hover:bg-[var(--hds-card)] transition-colors duration-500"
                        >
                            {open ? (
                                <X size={18} strokeWidth={1.5} />
                            ) : (
                                <Menu size={18} strokeWidth={1.5} />
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="mobile-menu"
                        data-testid="mobile-menu-panel"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 lg:hidden bg-[var(--hds-sand)]"
                    >
                        <div className="pt-[76px] h-full flex flex-col">
                            <nav
                                className="flex-1 flex flex-col justify-center px-8 gap-1"
                                aria-label="Mobiele navigatie"
                            >
                                {nav.map((item, i) => (
                                    <motion.div
                                        key={item.to}
                                        initial={{ opacity: 0, y: 18 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.6,
                                            delay: 0.05 + i * 0.04,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                    >
                                        <NavLink
                                            to={item.to}
                                            end={item.to === "/"}
                                            data-testid={`mobile-nav-link-${slugify(item.label)}`}
                                            className={({ isActive }) =>
                                                `block py-3 border-b border-[var(--hds-line)] font-[Fraunces] text-[28px] tracking-tight ${
                                                    isActive
                                                        ? "text-[var(--hds-clay)]"
                                                        : "text-[var(--hds-ink)]"
                                                }`
                                            }
                                            style={{ fontWeight: 300 }}
                                        >
                                            {item.label}
                                        </NavLink>
                                    </motion.div>
                                ))}
                            </nav>
                            <div className="p-6">
                                <Link
                                    to="/boeken"
                                    data-testid="mobile-cta-boek-sessie"
                                    className="w-full inline-flex items-center justify-center rounded-full bg-[var(--hds-clay)] text-[var(--hds-card)] py-4 text-[15px] tracking-wide"
                                >
                                    Boek een sessie
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
